import React from 'react';
import { Link } from 'react-router-dom';
import COMPANY, { ROUTES } from '../../config/company';

/**
 * SMS consent disclosures designed to meet Telnyx 10DLC review requirements; carrier approval is not guaranteed.
 *
 * Follows key documented Telnyx opt-in requirements:
 *  - TWO separate checkboxes: customer care (transactional) and marketing.
 *  - Both are UNCHECKED by default. Never pre-tick them.
 *  - Neither box is required to submit the Contact form. Consent is never a
 *    condition of purchase or of contacting us.
 *  - Every disclosure names the brand, lists message types, frequency,
 *    "Message and data rates may apply", STOP, HELP, "Consent is not a
 *    condition of purchase" and the no-sharing statement.
 *  - Privacy Policy, Terms & Conditions and SMS Terms are linked under the boxes.
 *
 * Keep the exact strings synchronized with the PHP consent-record endpoint,
 * the standalone public opt-in form, and the Telnyx campaign submission.
 */
export const CONSENT_VERSION = '2026-11-04-bitnexfreightllc-v2';

const LEGAL = COMPANY.brand;

export const SMS_CONSENT_TEXT = {
  customerCare:
    `By checking this box, I agree to receive SMS text messages from ${LEGAL} at the phone number provided about my trailer rental and inquiry, including customer care replies, reservation confirmations, pickup and return reminders, and account and billing notifications. ` +
    `Message frequency may vary. Message and data rates may apply. Reply STOP to opt out at any time. Reply HELP for help. ` +
    `Consent is not a condition of purchase. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.`,
  marketing:
    `By checking this box, I agree to receive marketing and promotional SMS text messages from ${LEGAL} at the phone number provided, including trailer availability, rental specials and promotional offers. You are opting in to marketing texts. ` +
    `Message frequency may vary, ${COMPANY.sms.marketingFrequency}. Message and data rates may apply. Reply STOP to opt out at any time. Reply HELP for help. ` +
    `Consent is not a condition of purchase. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.`,
};

/** Kept for older imports. */
export const CONSENT_TEXT = SMS_CONSENT_TEXT;

function ConsentBox({ id, name, checked, onChange, text, dark }) {
  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 text-[0.85rem] leading-relaxed ${
        dark ? 'border-white/15 bg-white/5 text-navy-100' : 'border-ink-200 bg-white text-ink-600'
      }`}
    >
      <input
        id={id}
        type="checkbox"
        name={name}
        value="yes"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
        aria-required="false"
        className="mt-1 h-5 w-5 shrink-0 cursor-pointer rounded border-ink-300 accent-[#15ae97]"
      />
      <span>{text}</span>
    </label>
  );
}

/**
 * Props:
 *  values   = { smsCustomerCare: bool, smsMarketing: bool }
 *  onChange = (field, checked) => void
 */
export default function SmsConsent({ values, onChange, idPrefix = 'sms', dark = false, className = '' }) {
  const link = dark ? 'text-gold-300 hover:text-gold-200' : 'text-navy-700 hover:text-navy-900';
  const muted = dark ? 'text-navy-200' : 'text-ink-500';
  return (
    <div id={`${idPrefix}-consent-block`} className={`space-y-3 ${className}`}>
      <div className={`text-[0.95rem] font-bold ${dark ? 'text-white' : 'text-navy-900'}`}>
        Text message updates (optional)
      </div>

      <ConsentBox
        id={`${idPrefix}_customer_care`}
        name="sms_customer_care"
        checked={values.smsCustomerCare}
        onChange={(v) => onChange('smsCustomerCare', v)}
        text={SMS_CONSENT_TEXT.customerCare}
        dark={dark}
      />
      <ConsentBox
        id={`${idPrefix}_marketing`}
        name="sms_marketing"
        checked={values.smsMarketing}
        onChange={(v) => onChange('smsMarketing', v)}
        text={SMS_CONSENT_TEXT.marketing}
        dark={dark}
      />

      <p className={`text-[0.82rem] leading-relaxed ${muted}`}>
        Carriers are not liable for delayed or undelivered messages. Reply HELP to our SMS messages for help, or email {COMPANY.emailDisplay}.
        See our{' '}
        <Link to={ROUTES.privacy} target="_blank" rel="noopener noreferrer" className={`font-semibold underline ${link}`}>Privacy Policy</Link>,{' '}
        <Link to={ROUTES.terms} target="_blank" rel="noopener noreferrer" className={`font-semibold underline ${link}`}>Terms &amp; Conditions</Link> and{' '}
        <Link to={ROUTES.smsTerms} target="_blank" rel="noopener noreferrer" className={`font-semibold underline ${link}`}>SMS Terms</Link>.
      </p>
    </div>
  );
}
